import { useMemo } from 'react';
import { VatMode } from '@insio/shared-types';
import { Select } from '../../../../components/selects/Select';
import { useTranslation } from '../../../../providers/UIProvider';
import { EditorProps } from '../types/editor-props';

/** Selectable modes — the legacy EXEMPT_* subtypes are display-only and no longer offered. */
const SELECTABLE_VAT_MODES: VatMode[] = [
  VatMode.STANDARD,
  VatMode.REVERSE_CHARGE,
  VatMode.EXEMPT_OTHER
];

/**
 * Dropdown editor for {@link VatMode}. Labels are read from the
 * `components.vatMode.{value}` namespace. Picking a value commits
 * immediately, like the other simple Select-based editors.
 */
export function VatModeEditor({ id, value, onChange, onCommit, onBlur }: EditorProps) {
  const t = useTranslation('components.vatMode');
  const current = value == null ? '' : String(value);
  const options = useMemo(() => {
    // A stored legacy subtype stays visible in the list so the current value renders correctly.
    const modes =
      current && !SELECTABLE_VAT_MODES.includes(current as VatMode)
        ? [...SELECTABLE_VAT_MODES, current as VatMode]
        : SELECTABLE_VAT_MODES;
    return modes.map(mode => ({
      value: mode,
      label: t(mode, { defaultValue: mode })
    }));
  }, [t, current]);

  return (
    <Select
      id={id}
      defaultOpen
      options={options}
      value={current}
      onChange={next => {
        const picked = Array.isArray(next) ? next[0] : next;
        const code = picked == null ? '' : String(picked);
        if (onCommit) {
          void onCommit(code).catch(() => {});
        } else {
          onChange(code);
        }
      }}
      onBlur={onBlur}
      autoFocus
    />
  );
}
